import React from 'react';
import {UseFormMethods} from 'react-hook-form';
import styled, {css} from 'styled-components';
import {useEnableBicField} from '../../../LegacyWebCoreConfigContext';
import TextInput from '../../Forms/Inputs/TextInput';
import {mediaMinTablet} from '../../constants/customMedia';
import {DisclaimerText} from '../CodBankData.styled';
import {useBankDetailsTranslationContext} from '../context/CodBankDataTranslation.context';
import {UpdateBankDataFormState} from '../hooks/useBankDataForm';

export interface BICTranslations {
  messages: {
    empty: string;
    invalid: string;
  };
  placeholder: string;
}

type FormContentProps = Pick<
  UseFormMethods<UpdateBankDataFormState>,
  'watch' | 'errors' | 'register'
> & {
  disabled?: boolean;
  singleInputPerLine?: boolean;
  orderId?: number;
  bicTranslations: BICTranslations;
};

export default function CodBankDataFormContent({
  register,
  errors,
  disabled,
  watch,
  singleInputPerLine,
  orderId,
  bicTranslations,
}: FormContentProps) {
  const {disclaimerText, formState, bicDisclaimerText} =
    useBankDetailsTranslationContext();
  const enableBicField = useEnableBicField();

  return (
    <WrapForm singleInputPerLine={singleInputPerLine}>
      {typeof orderId !== 'undefined' && (
        <input type="hidden" name="orderId" value={orderId} ref={register()} />
      )}
      <StyledTextInput
        value={watch('firstName')}
        name="firstName"
        data-testid="firstName"
        label={formState.firstName.placeholder}
        type="text"
        disabled={disabled}
        errorMessage={errors.firstName?.message}
        ref={register({
          required: formState.firstName.messages.empty,
        })}
      />
      <StyledTextInput
        value={watch('lastName')}
        name="lastName"
        data-testid="lastName"
        label={formState.lastName.placeholder}
        type="text"
        disabled={disabled}
        errorMessage={errors.lastName?.message}
        ref={register({
          required: formState.lastName.messages.empty,
        })}
      />
      <StyledTextInput
        name="iban"
        value={watch('iban')}
        label={formState.iban.placeholder}
        type="text"
        data-testid="iban"
        disabled={disabled}
        errorMessage={errors.iban?.message}
        ref={register({
          required: formState.iban.messages.empty,
        })}
      />
      {enableBicField && (
        <StyledTextInput
          name="bic"
          value={watch('bic')}
          label={bicTranslations.placeholder}
          type="text"
          data-testid="bic"
          disabled={disabled}
          errorMessage={errors.bic?.message}
          ref={register({
            required: enableBicField ? bicTranslations.messages.empty : false,
            validate: (value: string) => {
              if (!value) return true;
              return (
                /^[A-Z0-9]{8}$|^[A-Z0-9]{11}$/.test(value) ||
                bicTranslations.messages.invalid
              );
            },
          })}
        />
      )}
      {!enableBicField && (
        <DisclaimerText data-testid="bicDisclaimerText">
          {bicDisclaimerText}
        </DisclaimerText>
      )}
      <DisclaimerText data-testid="genericDisclaimerText">
        {disclaimerText}
      </DisclaimerText>
    </WrapForm>
  );
}

const StyledTextInput = styled(TextInput)``;

const WrapForm = styled.div<{singleInputPerLine?: boolean}>`
  display: flex;
  gap: 20px;
  flex-direction: column;
  @media ${mediaMinTablet} {
    flex-direction: row;
    flex-wrap: wrap;

    ${StyledTextInput} {
      flex-basis: 40%;
      flex-grow: 1;

      &:nth-child(odd) {
        margin-left: 20px;
      }

      ${props =>
        props.singleInputPerLine &&
        css`
          flex-basis: 100%;
          &:nth-child(odd) {
            margin-left: 0;
          }
        `}
    }
  }
`;
